import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SegmentedTabs from '../../../reusable/SegmentedTabs/SegmentedTabs';
import StatusChip from '../../../reusable/StatusChip/StatusChip';
import Button from '../../../reusable/Button/Button';
import { useTopbarSlot } from '../../../layout/TopbarSlot';
import { useGetCompetitionOverviewQuery } from '../../../store/api/competitionsApi';
import useCountdown from '../useCountdown';
import { statusOf, titleOf } from '../competition';
import { NA, usd } from '../../../utilities/format';
import { BannerSkeleton, CardsSkeleton, GridSkeleton } from './CompetitionsSkeleton';
import { t } from '../../../i18n/i18n';
import './Competitions.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The tablist: its labels are the keys, and its values are the endpoint's own
 * `tab` values — so pressing one names the list to ask for and the list to
 * read back out of `lists`.
 */
const TAB_KEY = { Joined: 'joined', Live: 'live', Upcoming: 'upcoming', Ended: 'ended' };

/** Each tab's empty state — whole sentences, so each reads right translated. */
const EMPTY_MESSAGE = {
  Joined: 'No joined competitions yet.',
  Live: 'No live competitions yet.',
  Upcoming: 'No upcoming competitions yet.',
  Ended: 'No ended competitions yet.',
};

// ponytail: badge colour/copy for `upcoming` and `ended` aren't in the
// reference screenshot (only `live` is shown across all 3 example cards) —
// first pass, confirm with design.
const STATUS_META = {
  live: { badgeLabel: 'Live now', badgeTone: 'success', buttonLabel: 'Open competition' },
  // `buttonLabel` is what an upcoming card says once the trader is in it; the
  // one they have not entered says `JOIN_LABEL` instead — see `CompetitionCard`.
  upcoming: { badgeLabel: 'Upcoming', badgeTone: 'info', buttonLabel: 'Open competition' },
  ended: { badgeLabel: 'Ended', badgeTone: 'neutral', buttonLabel: 'View results' },
};

/** An upcoming competition the trader has not entered yet. */
const JOIN_LABEL = 'Enter competition';

// en-GB is the only day-first English locale, and it abbreviates September as
// "Sept" — the Style Guide uses three letters everywhere.
const fmtDate = (value, opts) =>
  new Date(value)
    .toLocaleString('en-GB', { day: '2-digit', month: 'short', ...opts })
    .replace('Sept', 'Sep');

/**
 * The banner, 1:15405. `highest_profit` comes back null and `trades_executed`
 * zero on every competition so far — they are rendered as the endpoint answers
 * rather than hidden, so it is visible when the backend starts filling them.
 */
const BANNER_CELLS = [
  {
    label: 'Live competitions',
    value: (s) => s?.live_competitions ?? 0,
    note: () => t('accepting traders'),
  },
  {
    label: 'Traders competing',
    value: (s) => (s?.traders_competing ?? 0).toLocaleString('en-US'),
    note: (s) => t('across {{count}} events', { count: s?.live_competitions ?? 0 }),
  },
  {
    // The label is pre-formatted by the backend; the raw number is the fallback.
    label: 'Combined prize pool',
    value: (s) => s?.combined_prize_pool_label ?? usd(s?.combined_prize_pool),
    note: () => t('across live events'),
  },
  {
    label: 'Highest profit',
    value: (s) => (s?.highest_profit == null ? NA : usd(s.highest_profit)),
    note: () => t('best run so far'),
  },
  {
    label: 'Trades executed',
    value: (s) => (s?.trades_executed ?? 0).toLocaleString('en-US'),
    note: () => t('across live events'),
  },
];

/**
 * How far through its window a competition is. Off `starts_at` / `ends_at`,
 * not the `Start_date` / `End_Date` the card writes above the rail: those are
 * plain dates, and `End_Date` is midnight *opening* the closing day while
 * `ends_at` is the end of it, so the rail would fill early by nearly a day.
 */
const progressOf = (startsAt, endsAt) => {
  const start = Date.parse(startsAt ?? '');
  const end = Date.parse(endsAt ?? '');
  const span = end - start;
  if (!Number.isFinite(span) || span <= 0) return 100;
  return Math.min(100, Math.max(0, ((Date.now() - start) / span) * 100));
};

/** One competition card — 1:15405. `status` varies its badge/button/progress semantics. */
function CompetitionCard({ card }) {
  const navigate = useNavigate();
  const meta = STATUS_META[card.status] ?? STATUS_META.live;
  const upcoming = card.status === 'upcoming';

  // `starts_at` / `ends_at` are the competition's own instants, sent with an
  // offset, so the clock is right wherever the viewer is. They replaced the
  // `countdown` / `countdown_start` remaining-seconds objects the card used to
  // subtract from — the Overview and Detail heroes moved the same way.
  const target = Date.parse((upcoming ? card.starts_at : card.ends_at) ?? '');
  const { days, hours, minutes } = useCountdown(Number.isFinite(target) ? target : 0);

  // An upcoming card's button says which of the two things pressing it is
  // for: going in to enter, or going back to one already entered. `user` is
  // the trader's membership block and comes back null on a competition they
  // are not in — the Joined tab answers the same way without being asked,
  // since every row there carries one. Entering is only offered before the
  // start: a running competition reads as one to open, an ended one as
  // results to read.
  const buttonLabel =
    card.status === 'upcoming' && !card.user?.joined ? JOIN_LABEL : meta.buttonLabel;

  return (
    <article className="cc-card">
      <div className="cc-card__top">
        <span className="cc-status">
          <StatusChip tone={meta.badgeTone} size="sm">
            <span className={`cc-status__dot is-${card.status}`} />
            {t(meta.badgeLabel)}
          </StatusChip>
        </span>
        <span className="cc-card__type">
          {card.account_size_label ? t('{{size}} Challenge', { size: card.account_size_label }) : ''}
        </span>
      </div>

      <h3 className="cc-card__name">{titleOf(card.competition_name)}</h3>
      <p className="cc-card__range">
        {fmtDate(card.Start_date)} – {fmtDate(card.End_Date, { year: 'numeric' })}
      </p>

      {card.status === 'ended' ? (
        <>
          <div className="cc-card__row">
            <span className="cc-card__row-label">{t('Ended')}</span>
          </div>
          <span className="cc-rail">
            <span className="cc-rail__fill is-ended" style={{ width: '100%' }} />
          </span>
        </>
      ) : (
        <>
          <div className="cc-card__row">
            <span className="cc-card__row-label">{upcoming ? t('Starts in') : t('Ends in')}</span>
            <span className="cc-card__row-value">
              {t('{{days}}d {{hours}}h {{minutes}}m', { days, hours, minutes })}
            </span>
          </div>
          <span className="cc-rail">
            <span
              className="cc-rail__fill"
              style={{ width: `${upcoming ? 0 : progressOf(card.starts_at, card.ends_at)}%` }}
            />
          </span>
        </>
      )}

      <div className="cc-card__rule" />

      <div className="cc-card__stats">
        <span className="cc-card__stat">
          <span className="cc-card__stat-value">{(card.trader_count ?? 0).toLocaleString('en-US')}</span> {t('traders')}
        </span>
        <span className="cc-card__stat">
          <span className="cc-card__stat-value">{usd(card.prize_pool)}</span> {t('prize pool')}
        </span>
      </div>

      {/* Either way it lands on the detail page — which is where the entry
          form is, so "Enter competition" needs no destination of its own. */}
      <Button style={{ background: 'var(--bg-raised)', width: '100%' }} onClick={() => navigate(`/competition/${card.id}`)}>
        {t(buttonLabel)}
      </Button>
    </article>
  );
}

export default function Competitions() {
  // ---- Overview / Competitions tablist — lives in the top bar, 1:9020 ----
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname === '/competition' ? 'Overview' : 'Competitions';

  useTopbarSlot(
    useCallback(
      () => (
        <SegmentedTabs
          size="lg"
          radius="tab-track"
          className="competition-toggle"
          options={['Overview', 'Competitions'].map((v) => ({ value: v, label: t(v) }))}
          value={activeView}
          onChange={(next) => navigate(next === 'Competitions' ? '/competition/all' : '/competition')}
        />
      ),
      [activeView, navigate]
    ),
    [activeView]
  );

  const [tab, setTab] = useState('Joined');
  const tabKey = TAB_KEY[tab];

  // The tab is the request: `?tab=joined` and the server answers that list.
  // "Joined" in particular is its answer rather than a `participated` flag
  // filtered here. It still sends the other lists alongside, so the tab's own
  // one is read by name — the page draws what it asked for either way.
  const { data, isLoading, isFetching, isError } = useGetCompetitionOverviewQuery({
    tab: tabKey,
  });

  // The banner's figures don't vary by tab, so the last ones are held across a
  // switch: each tab is its own cache entry, and `data` is undefined while the
  // new one is in flight — without this the banner would blink back to skeleton
  // every time the grid below it changed.
  const lastStats = useRef(null);
  if (data?.stats) lastStats.current = data.stats;
  const stats = lastStats.current;

  // A row's own `status` decides its badge — the joined list mixes them. The
  // tabs are the only filter: a status dropdown beside them only repeated
  // what Upcoming / Live / Ended already split.
  //
  // A competition past its end reads as ended even while the backend still
  // lists it as live (`statusOf`, N64), and so it leaves the Live tab: it is
  // over, and it turns up under Ended once the backend catches up. The Joined
  // tab mixes statuses anyway, so there it simply carries the Ended badge.
  const activeList = useMemo(
    () =>
      (data?.lists?.[tabKey] ?? [])
        .map((c) => ({ ...c, status: statusOf(c) }))
        .filter((c) => tabKey !== 'live' || c.status === 'live'),
    [data, tabKey]
  );

  return (
    <div className="competition-list">
      {/* ---- Live stats banner, 1:15405 — /competitions/ ---- */}
      {isLoading && !stats ? <BannerSkeleton /> : (
      <section className="lsb">
        <header className="lsb__head">
          {/* ponytail: no clock icon exported yet — reusing the generic metrics
              glyph, same as Statistics/Trading objectives share one today. */}
          <span className="lsb__title">
            <span className="icon lsb__icon" style={icon('metrics-objectives')} />
            {t('Live right now')}
          </span>
          <span className="lsb__meta">{t('Updated live')}</span>
        </header>

        <div className="lsb__rule" />

        <div className="lsb__grid">
          {BANNER_CELLS.map((cell) => (
            <div className="lsb__cell" key={cell.label}>
              <span className="lsb__label">{t(cell.label)}</span>
              <span className="lsb__value">{cell.value(stats)}</span>
              <span className="lsb__note">{cell.note(stats)}</span>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ---- Joined / Upcoming / Ended, 1:15405 — /competitions/ ---- */}
      {isLoading ? <GridSkeleton /> : (
      <section className="cc-section">
        <header className="cc-section__head">
          <span className="cc-section__title">
            <span className="icon cc-section__icon" style={icon('nav-competition')} />
            {t(tab)} ({isFetching ? '—' : activeList.length})
          </span>
          <div className="cc-section__controls">
            <SegmentedTabs
              options={Object.keys(TAB_KEY).map((v) => ({ value: v, label: t(v) }))}
              value={tab}
              onChange={setTab}
            />
          </div>
        </header>

        {isFetching ? (
          <CardsSkeleton />
        ) : isError || activeList.length === 0 ? (
          <p className="cc-empty">
            {isError ? t('Could not load competitions.') : t(EMPTY_MESSAGE[tab])}
          </p>
        ) : (
          <div className="cc-grid">
            {activeList.map((card) => (
              <CompetitionCard key={card.id} card={card} />
            ))}
          </div>
        )}
      </section>
      )}
    </div>
  );
}
